import React, { useRef, useState, useMemo, useEffect } from "react";

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Grid,
	GridItem,
	Switch,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";


import { BarAndValue } from "common/components/BarAndValue";
import FullsizeSpinner from "common/components/FullsizeSpinner";
import { THUNK_STATE } from "config/types";
import { putCertificacionBulkThunk } from "store/portfolio/thunks";
import { useAppDispatch, useAppSelector } from "store/store";

type PendingAction = { toggleValue: boolean; type: "ite" | "bim" } | null;

export const PortfolioStatsCertificacion: React.FC = () => {
	const dispatch = useAppDispatch();
	const { portfolioParams } = useParams();
	const tipoIte = useMemo(() => {
		if (portfolioParams === "certificacion") return ["Principal", "Según estado"];
		if (portfolioParams === "certificacion-basicas") return ["Básica"];
		return undefined;
	}, [portfolioParams]);
	const { pagination } = useAppSelector((state) => state.portfolio.assetsInfo.data);
	const thunkState = useAppSelector((state) => state.portfolio.certificationActionThunkState);
	const portfolioStats = useAppSelector((state) => state.portfolio.portfolioStats.data);
	const features = useAppSelector((state) => state.user.features.data);
	const hasCertifyFeature = portfolioParams === "certificacion-basicas"
		? features.indexOf("manage-basica") >= 0 || features.indexOf("certify") >= 0
		: features.indexOf("certify") >= 0 && features.indexOf("manage-basica") < 0;

	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const [pendingAction, setPendingAction] = useState<PendingAction>(null);
	const [iteChecked, setIteChecked] = useState(false);
	const [bimChecked, setBimChecked] = useState(false);

	useEffect(() => {
		setIteChecked(false);
		setBimChecked(false);
		setPendingAction(null);
	}, [portfolioParams]);

	const numeroActivosSeleccionados = Math.max(0, pagination.total);

	const graphData = {
		"ITEs entregadas": portfolioStats?.estado_entrega_ite["Entregadas"] || 0,
		"ITEs certificadas": portfolioStats?.estado_certificacion_ite["Certificadas"] || 0,
		"BIM entregados": portfolioStats?.estado_entrega_bim["Entregadas"] || 0,
		"BIM certificados": portfolioStats?.estado_certificacion_bim["Certificadas"] || 0,
	};

	const requestBulkCertificate = (toggleValue: boolean, type: "ite" | "bim") => {
		setPendingAction({ toggleValue, type });
		onOpen();
	};

	const confirmBulkCertificate = async () => {
		if (!pendingAction) return;
		onClose();
		if (pendingAction.type === "ite") setIteChecked(pendingAction.toggleValue);
		else setBimChecked(pendingAction.toggleValue);
		const now = new Date().toISOString();
		await dispatch(
			putCertificacionBulkThunk({
				tipo: pendingAction.type,
				fecha: pendingAction.toggleValue ? now : undefined,
				tipoIte,
				view: portfolioParams,
			}),
		);
		setPendingAction(null);
	};

	const handleCancel = () => {
		onClose();
		setPendingAction(null);
	};

	const confirmationLabel = pendingAction
		? pendingAction.toggleValue
			? `Vas a certificar ${numeroActivosSeleccionados} ${
					pendingAction.type === "ite" ? "ITEs" : "modelos BIM"
			  }. Esta acción no se puede deshacer de forma masiva.`
			: `Vas a quitar la certificación de ${numeroActivosSeleccionados} ${
					pendingAction.type === "ite" ? "ITEs" : "modelos BIM"
			  }. Esta acción no se puede deshacer de forma masiva.`
		: "";

	if (!portfolioStats) return <React.Fragment />;
	return (
		<Flex flex="1">
			<Grid templateColumns="1fr 3fr" alignItems="center" pr="3rem" gap="0 1rem">
				{Object.entries(graphData).map(([key, value]) => (
					<React.Fragment key={key}>
						<GridItem>
							<Text textStyle="body3" color="gray.800">
								{key}
							</Text>
						</GridItem>
						<GridItem>
							<BarAndValue
								value={value}
								totalValue={numeroActivosSeleccionados}
								label={key}
								colors={{
									"ITEs entregadas": "green.300",
									"ITEs certificadas": "green.500",
									"BIM entregados": "teal.300",
									"BIM certificados": "teal.500",
								}}
							/>
						</GridItem>
					</React.Fragment>
				))}
			</Grid>
			{hasCertifyFeature && (
				<Grid templateColumns="1fr" maxW="15rem" gap=".5rem">
					<GridItem display="flex" alignItems="center">
						<FormControl display="flex" alignItems="center">
							<Box w="5.5rem">
								{thunkState === THUNK_STATE.PENDING ? <FullsizeSpinner color="green" /> : <React.Fragment />}
								<Switch
									id="certificar-todas-ite"
									isChecked={iteChecked}
									display={thunkState === THUNK_STATE.PENDING ? "none" : undefined}
									colorScheme="green"
									onChange={(e) => requestBulkCertificate(e.target.checked, "ite")}
								/>
							</Box>
							<FormLabel htmlFor="certificar-todas-ite" mb="0" ml="1rem" lineHeight=".8rem">
								{`Marcar las ${numeroActivosSeleccionados} ITEs seleccionados como certificadas`}
							</FormLabel>
						</FormControl>
					</GridItem>
					<GridItem display="flex" alignItems="center">
						<FormControl display="flex" alignItems="center">
							<Box w="5.5rem">
								{thunkState === THUNK_STATE.PENDING ? <FullsizeSpinner color="teal" /> : <React.Fragment />}
								<Switch
									id="certificar-todas-bim"
									isChecked={bimChecked}
									display={thunkState === THUNK_STATE.PENDING ? "none" : undefined}
									colorScheme="teal"
									onChange={(e) => requestBulkCertificate(e.target.checked, "bim")}
								/>
							</Box>
							<FormLabel htmlFor="certificar-todas-bim" mb="0" ml="1rem" lineHeight=".8rem">
								{`Marcar los ${numeroActivosSeleccionados} modelos BIM seleccionados como certificados`}
							</FormLabel>
						</FormControl>
					</GridItem>
				</Grid>
			)}

			<AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleCancel}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Confirmar cambio masivo
						</AlertDialogHeader>
						<AlertDialogBody>{confirmationLabel}</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={handleCancel}>
								Cancelar
							</Button>
							<Button colorScheme="green" onClick={confirmBulkCertificate} ml={3}>
								Confirmar
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
};
